// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the arithmetic behind "Time and client" (AgentTime.ts), shared by the API and the sandbox.
//
// A turn runs from the person's prompt to the last thing the agent did in it (its last step,
// reply or subagent finish); the latest turn uses the times the bridge reports (turnStartedAt /
// turnFinishedAt, or now while it runs). Turns the agent starts itself when background work comes
// back have no prompt and fold into the turn before, so a gap of over an hour inside a turn is
// treated as the agent waiting, not working, and is left out. Days are the person's own (their time
// zone); weeks start on Monday.

import type {AgentEstimateKnobs, AgentTimeClient, AgentTimeDay, AgentTimeTask, AgentYouBasis} from './AgentTime';

/** [start, end] in ms. */
export type TimeSeg = [number, number];

/** One turn: the person's prompt (its item id) and the stretches the agent worked in it. */
export interface TimeTurn {
	id: string;
	segs: Array<TimeSeg>;
	/** What the person did in the turn, for the estimate of their minutes (left out by older reads). */
	you?: YouTurn;
}

/** The person's part of a turn: when they sent it, what they wrote and the code they had to review. */
export interface YouTurn {
	/** When the prompt (or answer) was sent, ms. */
	at: number;
	/** Words in the prompt, at most AGENT_YOU_WORDS_PER_PROMPT (0 for an answer to the agent's questions). */
	words: number;
	/** Questions the turn answered (the prompt is the person's answer to them). */
	answers: number;
	/** Lines the agent changed in the turn (added plus removed), at most AGENT_YOU_LINES_PER_TURN. */
	lines: number;
}

/** Words counted per prompt: anything longer was pasted (a log, a spec), not written. */
export const AGENT_YOU_WORDS_PER_PROMPT = 300;
/** Changed lines counted per turn: past this it is generated code or a lock file, not read line by line. */
export const AGENT_YOU_LINES_PER_TURN = 2000;

/** Inside a turn, a longer pause than this is waiting, not working. */
export const AGENT_TIME_IDLE_GAP_MS = 60 * 60 * 1000;
/** Turns kept per chat. */
export const AGENT_TIME_TURNS_MAX = 400;

interface ItemLike {
	kind: string;
	id: string;
	createdAt: string;
	agents?: ReadonlyArray<{startedAt?: string; endedAt?: string}>;
	/** A prompt's words. */
	text?: string;
	/** Set when the prompt answers the agent's questions. */
	answers?: ReadonlyArray<unknown>;
	/** What a reply changed (the reply that ends a turn). */
	changes?: {additions: number; deletions: number};
}

const wordsIn = (text: string | undefined): number => (text?.match(/\S+/g) ?? []).length;

interface SummaryLike {
	status: string;
	turnStartedAt?: string;
	turnFinishedAt?: string;
}

const at = (value: string | undefined | null): number | null => {
	if (!value) return null;
	const ms = Date.parse(value);
	return Number.isFinite(ms) ? ms : null;
};

/** Splits a turn's moments into the stretches the agent worked (pauses over the idle gap left out). */
function segmentsOf(points: Array<number>): Array<TimeSeg> {
	const sorted = [...points].sort((a, b) => a - b);
	const segs: Array<TimeSeg> = [];
	let start = sorted[0];
	let previous = sorted[0];
	if (start === undefined || previous === undefined) return segs;
	for (const point of sorted.slice(1)) {
		if (point - previous > AGENT_TIME_IDLE_GAP_MS) {
			if (previous > start) segs.push([start, previous]);
			start = point;
		}
		previous = point;
	}
	if (previous > start) segs.push([start, previous]);
	return segs;
}

/**
 * The turns in a page (or all) of a thread's items, oldest first. Items before the first prompt
 * (the tail of an older turn) make a turn of their own, `head:<item id>`. `latest` says the items
 * end with the thread's latest turn, so its reported start and finish (or now, while it runs) count.
 */
export function threadTurns(
	items: ReadonlyArray<ItemLike>,
	summary: SummaryLike | null,
	now: number,
	latest = true,
): Array<TimeTurn> {
	const dated = items
		.map((item, index) => ({item, index, ms: at(item.createdAt)}))
		.filter((entry): entry is {item: ItemLike; index: number; ms: number} => entry.ms !== null)
		.sort((a, b) => a.ms - b.ms || a.index - b.index);
	const groups: Array<{id: string; points: Array<number>; you: YouTurn}> = [];
	for (const {item, ms} of dated) {
		const user = item.kind === 'user';
		if (user || groups.length === 0) {
			const answers = user ? (item.answers?.length ?? 0) : 0;
			groups.push({
				id: user ? item.id : `head:${item.id}`,
				points: [ms],
				you: {
					at: ms,
					words: user && answers === 0 ? Math.min(wordsIn(item.text), AGENT_YOU_WORDS_PER_PROMPT) : 0,
					answers,
					lines: 0,
				},
			});
		} else {
			const group = groups[groups.length - 1]!;
			group.points.push(ms);
			for (const agent of item.agents ?? []) {
				for (const point of [at(agent.startedAt), at(agent.endedAt)]) if (point !== null) group.points.push(point);
			}
		}
		if (item.changes) {
			const group = groups[groups.length - 1]!;
			const lines = Math.max(0, item.changes.additions) + Math.max(0, item.changes.deletions);
			group.you.lines = Math.min(AGENT_YOU_LINES_PER_TURN, group.you.lines + lines);
		}
	}
	const last = groups[groups.length - 1];
	if (latest && last && summary) {
		const started = at(summary.turnStartedAt);
		const finished = at(summary.turnFinishedAt);
		if (started !== null && started >= (last.points[0] ?? started) - 60_000) {
			last.points.push(started);
			if (finished !== null && finished >= started) last.points.push(finished);
		}
		if (summary.status === 'running' && (finished === null || (started !== null && finished < started))) {
			last.points.push(now);
		}
	}
	return groups.map((group) => ({id: group.id, segs: segmentsOf(group.points), you: group.you}));
}

/**
 * The stored turns brought up to date with freshly read ones (the newest pages). Turns older than
 * the first fresh one are kept as they were; `complete` (the read reached the thread's start)
 * replaces them all.
 */
export function mergeTurns(
	stored: ReadonlyArray<TimeTurn>,
	fresh: ReadonlyArray<TimeTurn>,
	complete: boolean,
): Array<TimeTurn> {
	if (complete || stored.length === 0) return fresh.slice(-AGENT_TIME_TURNS_MAX);
	const first = fresh.find((turn) => !turn.id.startsWith('head:'));
	let older: Array<TimeTurn>;
	const index = first ? stored.findIndex((turn) => turn.id === first.id) : -1;
	if (index >= 0) older = stored.slice(0, index);
	else {
		const freshStart = Math.min(...fresh.flatMap((turn) => turn.segs.map((seg) => seg[0])), Number.POSITIVE_INFINITY);
		const freshIds = new Set(fresh.map((turn) => turn.id));
		older = stored.filter(
			(turn) => !freshIds.has(turn.id) && turn.segs.every((seg) => seg[1] <= freshStart) && turn.segs.length > 0,
		);
	}
	// A head turn is the tail of the stored turn it continues: keep the stored one whole.
	const rest = fresh.filter((turn) => !turn.id.startsWith('head:') || older.length === 0);
	return [...older, ...rest].slice(-AGENT_TIME_TURNS_MAX);
}

export const turnsMs = (turns: ReadonlyArray<TimeTurn>): number =>
	turns.reduce((sum, turn) => sum + turn.segs.reduce((n, [s, e]) => n + Math.max(0, e - s), 0), 0);

export const promptTurns = (turns: ReadonlyArray<TimeTurn>): number =>
	turns.filter((turn) => !turn.id.startsWith('head:')).length;

// ---------------------------------------------------------------------------
// The person's own minutes: an estimate from what Konvo knows
//
// Konvo cannot see the person's screen, so their minutes on a chat are estimated from what it does
// see, with knobs the person can tweak (AgentEstimateKnobs, defaults AGENT_ESTIMATE_DEFAULTS):
//
//   minutes = prompts   × promptMinutes                 (1: reading the reply, deciding what next)
//           + words     ÷ wordsPerMinute                (30: writing the prompts)
//           + (answers + approvals) × answerMinutes     (0.5: each question answered, approval given)
//           + lines ÷ 100 × reviewMinutesPer100Lines    (4: reviewing the agent's changed lines)
//
// - prompts: turns the person started with a prompt of their own (an answer to the agent's
//   questions is an answer, not a prompt; turns the agent starts itself count for nothing but the
//   lines they change).
// - words: each prompt's words, at most AGENT_YOU_WORDS_PER_PROMPT (300): longer is pasted, not typed.
// - answers: the questions a turn answered; approvals: the approvals the person gave from Konvo.
// - lines: added plus removed lines of each turn's changes, at most AGENT_YOU_LINES_PER_TURN (2000).
// - Turns read before the estimate existed have no details: each counts as one prompt.
//
// The total is rounded to whole minutes (at least 1 when anything counted). Each turn's part sits
// on the day of its prompt, and each approval on its day, for the week and the digest; minutes the
// person set for a chat are spread over the same days in the same proportions.
//
// With the defaults: 10 prompts of 40 words, 2 answers and 600 changed lines come to
// 10 + 400/30 + 1 + 24 = 48.3, so "~48 min".

/** One part of the person's minutes, on the day of `at`. */
export interface YouPiece {
	at: number;
	minutes: number;
}

const isPrompt = (turn: TimeTurn): boolean => !turn.id.startsWith('head:') && (turn.you?.answers ?? 0) === 0;

/** The estimate's minutes for one turn. */
export function youTurnMinutes(turn: TimeTurn, knobs: AgentEstimateKnobs): number {
	if (!turn.you) return turn.id.startsWith('head:') ? 0 : knobs.promptMinutes;
	let minutes = 0;
	if (isPrompt(turn)) minutes += knobs.promptMinutes + turn.you.words / Math.max(1, knobs.wordsPerMinute);
	minutes += turn.you.answers * knobs.answerMinutes;
	minutes += (turn.you.lines / 100) * knobs.reviewMinutesPer100Lines;
	return minutes;
}

/** When the turn's prompt was sent (else when the agent started on it). */
const turnAt = (turn: TimeTurn): number | null => turn.you?.at ?? turn.segs[0]?.[0] ?? null;

/** The estimate per turn and per approval, dated (unrounded). */
export function youPieces(
	turns: ReadonlyArray<TimeTurn>,
	approvals: ReadonlyArray<number>,
	knobs: AgentEstimateKnobs,
): Array<YouPiece> {
	const pieces: Array<YouPiece> = [];
	for (const turn of turns) {
		const at = turnAt(turn);
		const minutes = youTurnMinutes(turn, knobs);
		if (at !== null && minutes > 0) pieces.push({at, minutes});
	}
	for (const at of approvals) if (knobs.answerMinutes > 0) pieces.push({at, minutes: knobs.answerMinutes});
	return pieces.sort((a, b) => a.at - b.at);
}

/** Whole minutes, at least 1 when there is anything. */
export const wholeMinutes = (minutes: number): number => (minutes <= 0 ? 0 : Math.max(1, Math.round(minutes)));

/** What the estimate rests on. */
export function youBasis(turns: ReadonlyArray<TimeTurn>, approvals: ReadonlyArray<number>): AgentYouBasis {
	const basis: AgentYouBasis = {prompts: 0, words: 0, answers: 0, approvals: approvals.length, lines: 0};
	for (const turn of turns) {
		if (isPrompt(turn)) basis.prompts += 1;
		basis.words += turn.you?.words ?? 0;
		basis.answers += turn.you?.answers ?? 0;
		basis.lines += turn.you?.lines ?? 0;
	}
	return basis;
}

/** The estimate from a chat's basis (the same sum as youPieces, for "How it's estimated"). */
export function youEstimateOfBasis(basis: AgentYouBasis, knobs: AgentEstimateKnobs): number {
	return (
		basis.prompts * knobs.promptMinutes +
		basis.words / Math.max(1, knobs.wordsPerMinute) +
		(basis.answers + basis.approvals) * knobs.answerMinutes +
		(basis.lines / 100) * knobs.reviewMinutesPer100Lines
	);
}

/**
 * The person's minutes on a chat as dated pieces: the estimate's, or the minutes they set spread in
 * the same proportions (all at `fallbackAt` when there is no estimate to follow).
 */
export function youMinutePieces(
	estimate: ReadonlyArray<YouPiece>,
	edited: number | null,
	fallbackAt: number,
): Array<YouPiece> {
	if (edited === null) return [...estimate];
	const total = estimate.reduce((n, piece) => n + piece.minutes, 0);
	if (edited <= 0) return [];
	if (total <= 0) return [{at: fallbackAt, minutes: edited}];
	return estimate.map((piece) => ({at: piece.at, minutes: (piece.minutes / total) * edited}));
}

/** The person's minutes on each of `dates` (their days). */
export function youMinutesOnDates(
	pieces: ReadonlyArray<YouPiece>,
	dates: ReadonlyArray<string>,
	tz: string,
): Array<number> {
	const wanted = new Map(dates.map((date) => [date, 0]));
	for (const piece of pieces) {
		const date = localDate(piece.at, tz);
		if (wanted.has(date)) wanted.set(date, wanted.get(date)! + piece.minutes);
	}
	return dates.map((date) => wanted.get(date) ?? 0);
}

/** Knobs as stored (any missing or out-of-range one falls back to its default). */
export function estimateKnobs(stored: unknown, defaults: AgentEstimateKnobs): AgentEstimateKnobs {
	const value = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : {};
	const pick = (name: keyof AgentEstimateKnobs, min: number, max: number) => {
		const raw = value[name];
		return typeof raw === 'number' && Number.isFinite(raw) && raw >= min && raw <= max ? raw : defaults[name];
	};
	return {
		promptMinutes: pick('promptMinutes', 0, 30),
		wordsPerMinute: pick('wordsPerMinute', 5, 200),
		answerMinutes: pick('answerMinutes', 0, 30),
		reviewMinutesPer100Lines: pick('reviewMinutesPer100Lines', 0, 120),
	};
}

/** The description on a time entry Konvo logs for the person's own minutes. */
export function youTimeDescription(title: string | null): string {
	const what = title?.trim() ? `: ${title.trim()}` : '';
	return `Prompting and reviewing the agent${what} (logged from Konvo Agents)`.slice(0, 500);
}

// ---------------------------------------------------------------------------
// Days in the person's time zone

/** A usable IANA zone, else UTC. */
export function safeZone(tz: string | null | undefined): string {
	if (!tz) return 'UTC';
	try {
		new Intl.DateTimeFormat('en-US', {timeZone: tz});
		return tz;
	} catch {
		return 'UTC';
	}
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsIn(ms: number, tz: string): {y: number; m: number; d: number; h: number; mi: number; s: number} {
	let format = formatters.get(tz);
	if (!format) {
		format = new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			hourCycle: 'h23',
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
		formatters.set(tz, format);
	}
	const parts = Object.fromEntries(format.formatToParts(new Date(ms)).map((part) => [part.type, part.value]));
	return {
		y: Number(parts.year),
		m: Number(parts.month),
		d: Number(parts.day),
		h: Number(parts.hour) % 24,
		mi: Number(parts.minute),
		s: Number(parts.second),
	};
}

const pad = (n: number) => String(n).padStart(2, '0');

/** The person's date at `ms`, YYYY-MM-DD. */
export function localDate(ms: number, tz: string): string {
	const p = partsIn(ms, tz);
	return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

/** When the person's day `date` (YYYY-MM-DD) starts, in ms. */
export function dayStart(date: string, tz: string): number {
	const [y, m, d] = date.split('-').map(Number) as [number, number, number];
	const guess = Date.UTC(y, m - 1, d);
	// The zone's offset near that midnight (twice, for a change of offset that day).
	let start = guess;
	for (let i = 0; i < 2; i++) {
		const p = partsIn(start, tz);
		const shown = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
		start = guess - (shown - start);
	}
	return start;
}

/** The date after `date`. */
export function nextDate(date: string): string {
	const [y, m, d] = date.split('-').map(Number) as [number, number, number];
	const next = new Date(Date.UTC(y, m - 1, d + 1));
	return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}

/** A stretch split at the person's midnights: [date, start, end] pieces. */
export function splitByDay([start, end]: TimeSeg, tz: string): Array<{date: string; start: number; end: number}> {
	const pieces: Array<{date: string; start: number; end: number}> = [];
	let from = start;
	let date = localDate(start, tz);
	while (from < end && pieces.length < 400) {
		const next = nextDate(date);
		const boundary = dayStart(next, tz);
		const to = Math.min(end, boundary > from ? boundary : end);
		pieces.push({date, start: from, end: to});
		from = to;
		date = next;
	}
	return pieces;
}

/** Agent time per day of the person's week (Monday to Sunday) that holds `now`. */
export function weekDates(now: number, tz: string): Array<string> {
	const today = localDate(now, tz);
	const [y, m, d] = today.split('-').map(Number) as [number, number, number];
	const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
	const back = (weekday + 6) % 7;
	const monday = new Date(Date.UTC(y, m - 1, d - back));
	const dates: Array<string> = [];
	let date = `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}`;
	for (let i = 0; i < 7; i++) {
		dates.push(date);
		date = nextDate(date);
	}
	return dates;
}

/** How much of the turns fell on each of `dates`. */
export function msOnDates(
	turns: ReadonlyArray<TimeTurn>,
	dates: ReadonlyArray<string>,
	tz: string,
): Array<AgentTimeDay> {
	const wanted = new Map(dates.map((date) => [date, 0]));
	const first = dates[0] ? dayStart(dates[0], tz) : 0;
	for (const turn of turns) {
		for (const seg of turn.segs) {
			if (seg[1] <= first) continue;
			for (const piece of splitByDay(seg, tz)) {
				if (wanted.has(piece.date)) wanted.set(piece.date, wanted.get(piece.date)! + (piece.end - piece.start));
			}
		}
	}
	return dates.map((date) => ({date, ms: wanted.get(date) ?? 0}));
}

/**
 * What Log time would log: the agent time after `loggedThrough` up to `upTo`, one piece per day so
 * each lands on its own day of the time sheet (starting when the agent started that day).
 */
export function unloggedPieces(
	turns: ReadonlyArray<TimeTurn>,
	loggedThrough: number | null,
	upTo: number,
	tz: string,
): Array<{date: string; start: number; end: number; ms: number}> {
	const byDate = new Map<string, {date: string; start: number; end: number; ms: number}>();
	for (const turn of turns) {
		for (const [s, e] of turn.segs) {
			const start = Math.max(s, loggedThrough ?? Number.NEGATIVE_INFINITY);
			const end = Math.min(e, upTo);
			if (end <= start) continue;
			for (const piece of splitByDay([start, end], tz)) {
				const current = byDate.get(piece.date);
				if (current) {
					current.start = Math.min(current.start, piece.start);
					current.end = Math.max(current.end, piece.end);
					current.ms += piece.end - piece.start;
				} else byDate.set(piece.date, {...piece, ms: piece.end - piece.start});
			}
		}
	}
	return [...byDate.values()].sort((a, b) => a.start - b.start);
}

// ---------------------------------------------------------------------------
// Clients and tasks

export const foldName = (name: string): string => name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();

/** The id of a client named in the task or typed in. */
export const namedClientId = (name: string): string => `name:${foldName(name).slice(0, 190)}`;

interface TaskFieldLike {
	name: string;
	type: string;
	value: unknown;
	options?: ReadonlyArray<{name: string}>;
}

/** A ClickUp task as Konvo normalises it (KonvoClickUpNormalize.normalizeTask), the parts used here. */
export interface TaskLike {
	id: string | null;
	name: string;
	custom_id?: string | null;
	url?: string | null;
	status?: {status?: string | null; color?: string | null; type?: string | null} | null;
	list?: {id?: string | null; name?: string | null} | null;
	/** The list's statuses in ClickUp order, when Konvo read them (they place the status ring's fill). */
	list_statuses?: ReadonlyArray<{status?: string | null; type?: string | null}> | null;
	folder?: {id?: string | null; name?: string | null} | null;
	space?: {id?: string | null; name?: string | null} | null;
	custom_fields?: ReadonlyArray<TaskFieldLike> | null;
}

/** A saved client (dashboards/ClientStore): its id, name and ClickUp places. */
export interface SavedClientLike {
	client_id: string;
	name: string;
	clickup: ReadonlyArray<{kind: string; id: string}>;
}

function fieldText(field: TaskFieldLike): string | null {
	const options = (field.options ?? []).map((option) => option.name.trim()).filter(Boolean);
	if (options.length) return options.join(', ');
	if (typeof field.value === 'string' && field.type !== 'drop_down' && field.type !== 'labels') {
		return field.value.trim() || null;
	}
	return null;
}

/** The task's Billability field, e.g. "Billable". */
export function billabilityOf(task: TaskLike): string | null {
	const field = (task.custom_fields ?? []).find((entry) => /billab/i.test(entry.name));
	return field ? fieldText(field) : null;
}

/**
 * Who the task is for: a saved client that holds its list, folder or space; else the task's Client
 * field; else the client a ticketing list is named for ("iCAM Ticketing"); else its space.
 */
export function clientOfTask(task: TaskLike, saved: ReadonlyArray<SavedClientLike>): AgentTimeClient | null {
	const places = new Set(
		[
			task.list?.id ? `list:${task.list.id}` : null,
			task.folder?.id ? `folder:${task.folder.id}` : null,
			task.space?.id ? `space:${task.space.id}` : null,
		].filter(Boolean),
	);
	const match = saved.find((client) => client.clickup.some((scope) => places.has(`${scope.kind}:${scope.id}`)));
	if (match) return {id: `client:${match.client_id}`, name: match.name.slice(0, 120)};
	const field = (task.custom_fields ?? []).find((entry) => /^(client|customer)( name)?$/i.test(entry.name.trim()));
	const named = field ? fieldText(field) : null;
	if (named) return {id: namedClientId(named), name: named.slice(0, 120)};
	const ticketing = /^(.+?)\s+ticketing$/i.exec(task.list?.name?.trim() ?? '');
	if (ticketing?.[1]) return {id: namedClientId(ticketing[1]), name: ticketing[1].slice(0, 120)};
	if (task.space?.id && task.space.name?.trim()) {
		return {id: `space:${task.space.id}`, name: task.space.name.trim().slice(0, 120)};
	}
	return null;
}

const isFinishedType = (type: string | null | undefined) => type === 'closed' || type === 'done';

/**
 * How far along its list a status is, 0 (open) to 1 (done), like the Tasks page's status ring: the
 * place among the list's open statuses, kept between a quarter and most of the way; half without them.
 */
export function statusFill(
	status: {status?: string | null; type?: string | null},
	statuses?: ReadonlyArray<{status?: string | null; type?: string | null}> | null,
): number {
	if (isFinishedType(status.type)) return 1;
	if (status.type === 'open') return 0;
	const open = (statuses ?? []).filter((entry) => !isFinishedType(entry.type));
	const name = (status.status ?? '').toLowerCase();
	const index = open.findIndex((entry) => (entry.status ?? '').toLowerCase() === name);
	if (index < 0 || open.length < 2) return 0.5;
	return Math.round(Math.min(0.85, Math.max(0.25, index / (open.length - 1))) * 100) / 100;
}

/** The task as the time panel shows it. */
export function timeTask(task: TaskLike): AgentTimeTask {
	return {
		id: String(task.id ?? '').slice(0, 60),
		name: task.name.slice(0, 500),
		customId: task.custom_id ? task.custom_id.slice(0, 60) : null,
		url: task.url ? task.url.slice(0, 500) : null,
		status: task.status?.status
			? {
					name: task.status.status.slice(0, 100),
					color: task.status.color?.slice(0, 20) ?? null,
					type: task.status.type?.slice(0, 20) ?? null,
					fill: statusFill(task.status, task.list_statuses),
				}
			: null,
		billability: billabilityOf(task)?.slice(0, 200) ?? null,
		list: task.list?.name?.slice(0, 200) ?? null,
		space: task.space?.name?.slice(0, 200) ?? null,
	};
}

/** The description on a time entry Konvo logs for agent time. */
export function agentTimeDescription(title: string | null): string {
	const what = title?.trim() ? `: ${title.trim()}` : '';
	return `Agent time${what} (logged from Konvo Agents)`.slice(0, 500);
}
